import { LitElement, html, nothing } from 'lit';
import { t } from '../i18n';
import { apiGetDedupe } from '../api';
import { showToast } from '../toast';
import { defineZenComponent } from './register';
import { presentWritingStyle, type WritingStyleChip } from '../style-profile-presentation';

class ZenStyleProfileSection extends LitElement {
  static properties = {
    loading: { attribute: false },
    chips: { attribute: false },
    analyzedAt: { attribute: false },
    empty: { attribute: false },
  };

  declare loading: boolean;
  declare chips: WritingStyleChip[];
  declare analyzedAt: string | undefined;
  declare empty: boolean;
  private writingStyleSource: unknown;

  constructor() {
    super();
    this.loading = false;
    this.chips = [];
    this.analyzedAt = undefined;
    this.empty = true;
    this.writingStyleSource = null;
  }

  createRenderRoot() {
    return this;
  }

  private _onLangChange = () => {
    this.applyPresentedWritingStyle(this.writingStyleSource);
    this.requestUpdate();
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('zen-lang-change', this._onLangChange);
    void this.load();
  }

  disconnectedCallback() {
    document.removeEventListener('zen-lang-change', this._onLangChange);
  }

  private applyPresentedWritingStyle(source: unknown) {
    const presented = presentWritingStyle(source, {
      tone: t('styleProfileToneAxis') as string,
      sentenceLength: t('styleProfileSentenceLengthAxis') as string,
      formality: t('styleProfileFormalityAxis') as string,
    });
    this.chips = presented.chips;
    this.analyzedAt = presented.analyzedAt;
    this.empty = presented.empty;
  }

  private async load() {
    this.loading = true;
    this.requestUpdate();
    try {
      const data = await apiGetDedupe('/api/profile') as any;
      this.writingStyleSource = data?.profile?.writing_style ?? null;
      this.applyPresentedWritingStyle(this.writingStyleSource);
    } catch {
      showToast(t('styleProfileLoadError') as string, 'error');
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  private formatDate(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
  }

  private renderChips() {
    return html`
      <div class="chips">
        ${this.chips.map((chip) => html`<span class="chip"><span class="mi">${chip.icon}</span>${chip.label}</span>`)}
      </div>
    `;
  }

  private async copyStyleAnalyzerPrompt() {
    const text = t('styleProfileAnalyzerPrompt') as string;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      showToast(t('clipboardWriteFailed') as string, 'error');
      return;
    }
    showToast(t('copyClaudeDesktop') as string);
    window.open('https://claude.ai/new', '_blank');
  }

  private renderEmptyState() {
    return html`
      <div class="settings-empty-action">
        <span class="mi" aria-hidden="true">edit_note</span>
        <div>
          <div class="settings-empty-action-title">${t('styleProfileEmptyTitle')}</div>
          <div class="settings-empty-action-copy">${t('styleProfileEmptyDescription')}</div>
        </div>
        <button type="button" class="btn btn-sm btn-outline" @click=${() => this.copyStyleAnalyzerPrompt()}>
          <span class="mi mi-xs" aria-hidden="true">content_copy</span>${t('styleProfileCopyPromptAction')}
        </button>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`<div class="settings-section-placeholder">${t('styleProfileLoading')}</div>`;
    }

    return html`
      <div class="group-note">
        <span class="mi">info</span>
        <span>${t('styleProfileNote')}</span>
      </div>
      ${this.empty
        ? this.renderEmptyState()
        : html`
          <div class="field-row">
            <span class="field-label">
              ${t('styleProfileResultLabel')}
              ${this.analyzedAt ? html`<span class="field-hint">${t('styleProfileAnalyzedAt', { date: this.formatDate(this.analyzedAt) })}</span>` : nothing}
            </span>
            <div class="field-control">${this.renderChips()}</div>
          </div>
        `}
      ${this.empty ? nothing : html`
        <div class="group-note">
          <span class="mi">info</span>
          <span>${t('styleProfileReanalyzeInfo')}</span>
        </div>
      `}
    `;
  }
}

defineZenComponent('zen-style-profile-section', ZenStyleProfileSection);
